import { Card, Text } from '@/components';
import type { ActivityFlowNodeModel } from '@/features/project/projects/_models/activity';
import { type CSSProperties, type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, type NodeProps, Position } from 'reactflow';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { getNodeName } from '../../../utils';

type CustomNodeProps = {
  node: z.infer<typeof ActivityFlowNodeModel>;
};

const MAXIMUM_TEXT_ROWS = 5;
const cardStyle: CSSProperties = {
  width: 290,
  textAlign: 'center',
};
const cardBodyStyle: CSSProperties = {
  border: '1px solid #16828b',
  textAlign: 'left',
};
const cardHeaderStyle: CSSProperties = {
  border: '1px solid #16828b',
};

const _CustomNode: FC<NodeProps<CustomNodeProps>> = ({ data: { node } }) => {
  const { t } = useTranslation();

  invariant(
    !!node.activity || !!node.indicator,
    t('error-message-unknown-node-type'),
  );

  const nodeName = getNodeName(node.activity, node.indicator);

  if (node.activity !== null) {
    return (
      <Card
        style={cardStyle}
        styles={{
          body: cardBodyStyle,
          header: cardHeaderStyle,
        }}
        title={<Text variant="h5" text={nodeName} />}
      >
        <Handle type="target" position={Position.Left} />
        <Text
          variant="h5"
          text={node.activity.activityContent}
          ellipsis
          maxRows={MAXIMUM_TEXT_ROWS}
        />
        <Handle type="source" position={Position.Right} />
      </Card>
    );
  }

  if (node.indicator?.type === 'output') {
    invariant(node.indicator.sortOrder, t('unreachable-code'));
    return (
      <Card
        style={cardStyle}
        styles={{ body: cardBodyStyle, header: cardHeaderStyle }}
        title={<Text variant="h5" text={nodeName} />}
      >
        <Handle type="target" position={Position.Left} />
        <Text
          variant="h5"
          text={node.indicator.title}
          ellipsis
          maxRows={MAXIMUM_TEXT_ROWS}
        />
        <Handle type="source" position={Position.Right} />
      </Card>
    );
  }

  if (node.indicator?.type === 'outcome') {
    invariant(node.indicator.sortOrder, t('unreachable-code'));

    return (
      <Card
        style={cardStyle}
        styles={{ body: cardBodyStyle, header: cardHeaderStyle }}
        title={<Text variant="h5" text={nodeName} />}
      >
        <Handle type="target" position={Position.Left} />
        <Text
          variant="h5"
          text={node.indicator.title}
          ellipsis
          maxRows={MAXIMUM_TEXT_ROWS}
        />
        <Handle type="source" position={Position.Right} />
      </Card>
    );
  }

  return null;
};

export const CustomNode = memo(_CustomNode);
